export const WS_EVENT_PING = 9000;
export const WS_EVENT_PONG = 9001;

export const SMT_DEVICE_ID_KEY = 'smt_wsb_device_id';
const TOKEN_STORAGE_KEY = 'dw__t';
const TOKEN_FALLBACK_KEY = 'token';
const HEARTBEAT_INTERVAL_TEST_MS = 10000;
const HEARTBEAT_INTERVAL_PROD_MS = 30000;
const RECONNECT_INTERVAL_MS = 3000;
const RECONNECT_MAX_ATTEMPTS = 10;
const BUSINESS_MESSAGE_BUFFER_SIZE = 50;

export interface SmtWsMessage {
  frp: string;
  time: number;
  event: number;
  content: string | Record<string, unknown> | null;
}

type MessageHandler = (message: SmtWsMessage) => void;

export function parseSmtWsContent(
  content: SmtWsMessage['content'],
): Record<string, unknown> | null {
  if (content == null || content === '') {
    return null;
  }
  if (typeof content === 'object') {
    return content;
  }
  try {
    const parsed = JSON.parse(content);
    return typeof parsed === 'object' && parsed != null ? parsed : { value: parsed };
  } catch (error) {
    return { value: content };
  }
}

function normalizeEvent(event: unknown): number {
  const value = Number(event);
  return Number.isFinite(value) ? value : -1;
}

function createUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

let cachedDeviceId: string | null = null;

function readDeviceIdFromStorage(): string | null {
  try {
    const value = localStorage.getItem(SMT_DEVICE_ID_KEY);
    if (value == null) {
      return null;
    }
    const trimmed = value.trim();
    return trimmed || null;
  } catch {
    return null;
  }
}

function writeDeviceIdToStorage(deviceId: string): void {
  try {
    localStorage.setItem(SMT_DEVICE_ID_KEY, deviceId);
  } catch (error) {
    console.warn('[SmtWebSocket] 无法写入 deviceId，将仅在本页会话内复用', error);
  }
}

function resolveDeviceId(): string {
  if (cachedDeviceId) {
    return cachedDeviceId;
  }

  const storedDeviceId = readDeviceIdFromStorage();
  if (storedDeviceId) {
    cachedDeviceId = storedDeviceId;
    return storedDeviceId;
  }

  const deviceId = createUuid();
  cachedDeviceId = deviceId;
  writeDeviceIdToStorage(deviceId);
  return deviceId;
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== SMT_DEVICE_ID_KEY || !event.newValue) {
      return;
    }
    const nextDeviceId = event.newValue.trim();
    if (nextDeviceId) {
      cachedDeviceId = nextDeviceId;
    }
  });
}

function getWsBaseUrl(): string {
  if (process.env.NODE_ENV === 'development') {
    return 'ws://testpc.dawei.art:1546';
  }
  // 测试包仍走测试 ws；正式包（含未注入 VUE_APP_TITLE 的默认 production 构建）走 wss，避免 HTTPS 页 Mixed Content
  if (process.env.NODE_ENV === 'production' && process.env.VUE_APP_TITLE === 'test') {
    return 'ws://testpc.dawei.art:1546';
  }
  if (process.env.NODE_ENV === 'production') {
    return 'wss://smtsocket.dawei.art';
  }
  return 'ws://testpc.dawei.art:1546';
}

function isTestEnv(): boolean {
  if (process.env.NODE_ENV === 'development') {
    return true;
  }
  return process.env.NODE_ENV === 'production' && process.env.VUE_APP_TITLE === 'test';
}

function getHeartbeatIntervalMs(): number {
  return isTestEnv() ? HEARTBEAT_INTERVAL_TEST_MS : HEARTBEAT_INTERVAL_PROD_MS;
}

class SmtWebSocket {
  private socket: WebSocket | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private manualClose = false;
  private autoReconnectEnabled = true;
  private messageHandlers: Map<number, Set<MessageHandler>> = new Map();
  private anyMessageHandlers: Set<MessageHandler> = new Set();
  private businessMessageBuffer: SmtWsMessage[] = [];
  private openHandlers: Set<() => void> = new Set();
  private closeHandlers: Set<() => void> = new Set();
  private connectionChangeHandlers: Set<(connected: boolean) => void> = new Set();
  private connectionNotified = false;

  getDeviceId(): string {
    return resolveDeviceId();
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_STORAGE_KEY) || localStorage.getItem(TOKEN_FALLBACK_KEY);
  }

  buildUrl(): string | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }
    const deviceId = this.getDeviceId();
    const baseUrl = getWsBaseUrl();
    const query = `token=${encodeURIComponent(token)}&platform=ats`;
    return `${baseUrl}/wsb-smt/${deviceId}?${query}`;
  }

  isConnected(): boolean {
    return this.socket != null && this.socket.readyState === WebSocket.OPEN;
  }

  setAutoReconnect(enabled: boolean): void {
    this.autoReconnectEnabled = enabled;
    if (!enabled) {
      this.clearReconnectTimer();
    } else if (!this.isConnected() && !this.manualClose) {
      this.scheduleReconnect();
    }
  }

  ensureConnected(): void {
    this.manualClose = false;
    this.autoReconnectEnabled = true;
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();
    this.connect();
  }

  onConnectionChange(handler: (connected: boolean) => void): () => void {
    this.connectionChangeHandlers.add(handler);
    return () => this.connectionChangeHandlers.delete(handler);
  }

  private resetConnectionNotified(): void {
    this.connectionNotified = false;
  }

  private notifyConnected(): void {
    if (!this.isConnected() || this.connectionNotified) {
      return;
    }
    this.connectionNotified = true;
    this.reconnectAttempts = 0;
    this.startHeartbeat();
    this.sendPing();
    this.openHandlers.forEach((handler) => handler());
    this.connectionChangeHandlers.forEach((handler) => handler(true));
  }

  private notifyDisconnected(): void {
    this.connectionNotified = false;
    this.closeHandlers.forEach((handler) => handler());
    this.connectionChangeHandlers.forEach((handler) => handler(false));
  }

  connect(force = false): void {
    if (!force && this.isConnected()) {
      this.notifyConnected();
      return;
    }

    const url = this.buildUrl();
    if (!url) {
      console.warn('[SmtWebSocket] 缺少 token，跳过连接');
      return;
    }

    this.manualClose = false;
    this.clearReconnectTimer();
    this.resetConnectionNotified();

    if (this.socket) {
      const staleSocket = this.socket;
      this.socket = null;
      this.stopHeartbeat();
      staleSocket.onopen = null;
      staleSocket.onmessage = null;
      staleSocket.onerror = null;
      staleSocket.onclose = null;
      staleSocket.close();
    }

    let currentSocket: WebSocket;
    try {
      currentSocket = new WebSocket(url);
      this.socket = currentSocket;
    } catch (error) {
      this.socket = null;
      console.error('[SmtWebSocket] 创建连接失败', error);
      this.scheduleReconnect();
      return;
    }

    currentSocket.onopen = () => {
      if (this.socket !== currentSocket) {
        return;
      }
      this.notifyConnected();
    };

    currentSocket.onmessage = (event: MessageEvent) => {
      if (this.socket !== currentSocket) {
        return;
      }
      this.handleIncomingMessage(event.data);
    };

    currentSocket.onerror = (error) => {
      if (this.socket !== currentSocket) {
        return;
      }
      console.error('[SmtWebSocket] 连接异常', error);
    };

    currentSocket.onclose = () => {
      if (this.socket !== currentSocket) {
        return;
      }
      this.stopHeartbeat();
      this.socket = null;
      this.notifyDisconnected();
      if (!this.manualClose) {
        this.scheduleReconnect();
      }
    };
  }

  disconnect(): void {
    this.manualClose = true;
    this.autoReconnectEnabled = false;
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();
    this.stopHeartbeat();
    this.resetConnectionNotified();
    if (this.socket) {
      const closingSocket = this.socket;
      this.socket = null;
      closingSocket.onopen = null;
      closingSocket.onmessage = null;
      closingSocket.onerror = null;
      closingSocket.onclose = null;
      closingSocket.close();
    }
  }

  reconnect(): void {
    this.disconnect();
    this.manualClose = false;
    this.connect(true);
  }

  onOpen(handler: () => void): () => void {
    this.openHandlers.add(handler);
    if (this.connectionNotified) {
      handler();
    }
    return () => this.openHandlers.delete(handler);
  }

  onClose(handler: () => void): () => void {
    this.closeHandlers.add(handler);
    return () => this.closeHandlers.delete(handler);
  }

  onEvent(event: number, handler: MessageHandler): () => void {
    if (!this.messageHandlers.has(event)) {
      this.messageHandlers.set(event, new Set());
    }
    this.messageHandlers.get(event)!.add(handler);
    this.replayBusinessMessages((message) => message.event === event, handler);
    return () => this.messageHandlers.get(event)?.delete(handler);
  }

  onAnyMessage(handler: MessageHandler): () => void {
    this.anyMessageHandlers.add(handler);
    this.replayBusinessMessages(() => true, handler);
    return () => this.anyMessageHandlers.delete(handler);
  }

  getBufferedBusinessMessages(): SmtWsMessage[] {
    return [...this.businessMessageBuffer];
  }

  send(event: number, content: SmtWsMessage['content'] = ''): boolean {
    if (!this.isConnected()) {
      return false;
    }

    const payload: SmtWsMessage = {
      frp: createUuid(),
      time: Date.now(),
      event,
      content,
    };

    this.socket!.send(JSON.stringify(payload));
    return true;
  }

  sendPing(): boolean {
    return this.send(WS_EVENT_PING, '');
  }

  private handleIncomingMessage(raw: string): void {
    this.notifyConnected();

    let parsed: Partial<SmtWsMessage>;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      console.warn('[SmtWebSocket] 无法解析消息', raw);
      return;
    }

    const event = normalizeEvent(parsed.event);
    if (event === WS_EVENT_PING) {
      this.send(WS_EVENT_PONG, parsed.content ?? '');
      return;
    }
    if (event === WS_EVENT_PONG) {
      return;
    }

    const message: SmtWsMessage = {
      frp: String(parsed.frp || ''),
      time: Number(parsed.time) || Date.now(),
      event,
      content: parsed.content ?? null,
    };

    if (process.env.NODE_ENV === 'development') {
      console.info('[SmtWebSocket] 收到业务消息', message.event, parseSmtWsContent(message.content));
    }

    this.bufferBusinessMessage(message);
    this.dispatchBusinessMessage(message);
  }

  private bufferBusinessMessage(message: SmtWsMessage): void {
    this.businessMessageBuffer.push(message);
    if (this.businessMessageBuffer.length > BUSINESS_MESSAGE_BUFFER_SIZE) {
      this.businessMessageBuffer.shift();
    }
  }

  private replayBusinessMessages(
    matcher: (message: SmtWsMessage) => boolean,
    handler: MessageHandler,
  ): void {
    this.businessMessageBuffer.forEach((message) => {
      if (matcher(message)) {
        handler(message);
      }
    });
  }

  private dispatchBusinessMessage(message: SmtWsMessage): void {
    this.messageHandlers.get(message.event)?.forEach((handler) => handler(message));
    this.anyMessageHandlers.forEach((handler) => handler(message));
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    const intervalMs = getHeartbeatIntervalMs();
    this.heartbeatTimer = setInterval(() => {
      if (!this.isConnected()) {
        this.stopHeartbeat();
        return;
      }
      this.sendPing();
    }, intervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.manualClose || !this.autoReconnectEnabled || this.reconnectTimer) {
      return;
    }
    if (this.reconnectAttempts >= RECONNECT_MAX_ATTEMPTS) {
      console.warn(`[SmtWebSocket] 已达到最大重连次数（${RECONNECT_MAX_ATTEMPTS}次）`);
      return;
    }

    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect(true);
    }, RECONNECT_INTERVAL_MS);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

const smtWebSocket = new SmtWebSocket();

export default smtWebSocket;
